import React, { useState, useEffect, useCallback } from 'react';
import { Bell, BellRing, X, CheckCircle, AlertTriangle, Clock, TrendingDown } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { getAuthToken } from '../utils/authStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function NotificationBell({ isDark }) {
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [lastChecked, setLastChecked] = useState(Date.now());

  // Request notification permission
  const requestPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('المتصفح لا يدعم الإشعارات');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      if (permission === 'granted') {
        toast.success('تم تفعيل الإشعارات');
        // Send test notification
        new Notification('شركة البريق', {
          body: 'تم تفعيل الإشعارات بنجاح!',
          icon: '/favicon.ico',
          tag: 'test'
        });
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    }
  };

  // Show browser notification
  const showBrowserNotification = useCallback((alert) => {
    if (notificationPermission !== 'granted') return;

    const notification = new Notification(alert.title, {
      body: alert.message,
      icon: '/favicon.ico',
      tag: alert.id,
      requireInteraction: alert.severity === 'high'
    });

    notification.onclick = () => {
      window.focus();
      window.location.href = '/smart-alerts';
      notification.close();
    };
  }, [notificationPermission]);

  // Fetch alerts
  const fetchAlerts = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const [alertsRes, countRes] = await Promise.all([
        fetch(`${API_URL}/api/smart-alerts?unread_only=false&limit=5`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/smart-alerts/count`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (alertsRes.ok && countRes.ok) {
        const alertsData = await alertsRes.json();
        const countData = await countRes.json();

        // Check for new high priority alerts
        const newAlerts = alertsData.filter(a => 
          !a.is_read && 
          a.severity === 'high' && 
          new Date(a.created_at).getTime() > lastChecked
        );

        // Show browser notifications for new high priority alerts
        newAlerts.forEach(alert => {
          showBrowserNotification(alert);
        });

        setAlerts(alertsData);
        setUnreadCount(countData.count || 0);
        setLastChecked(Date.now());
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  }, [lastChecked, showBrowserNotification]);

  // Check notification permission on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  // Fetch alerts on mount and periodically
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  // Mark as read
  const markAsRead = async (alertId) => {
    try {
      const token = getAuthToken();
      await fetch(`${API_URL}/api/smart-alerts/${alertId}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, is_read: true } : a));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'low_performance': return <TrendingDown size={16} className="text-red-500" />;
      case 'consecutive_absence': return <AlertTriangle size={16} className="text-orange-500" />;
      case 'frequent_lateness': return <Clock size={16} className="text-yellow-500" />;
      default: return <AlertTriangle size={16} className="text-blue-500" />;
    }
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      high: 'bg-red-500',
      medium: 'bg-orange-500',
      low: 'bg-yellow-500'
    };
    return colors[severity] || 'bg-blue-500';
  };

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative"
        data-testid="notification-bell"
      >
        {unreadCount > 0 ? (
          <BellRing className={`h-5 w-5 ${isDark ? 'text-white' : 'text-slate-600'} animate-pulse`} />
        ) : (
          <Bell className={`h-5 w-5 ${isDark ? 'text-white' : 'text-slate-600'}`} />
        )}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {showDropdown && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setShowDropdown(false)}
          />
          <div className={`absolute left-0 mt-2 w-80 rounded-lg shadow-lg z-50 ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-gray-200'}`}>
            {/* Header */}
            <div className={`flex items-center justify-between p-3 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
              <h3 className={`font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>التنبيهات</h3>
              <div className="flex items-center gap-2">
                {notificationPermission !== 'granted' && (
                  <Button variant="ghost" size="sm" onClick={requestPermission} className="text-xs">
                    تفعيل الإشعارات
                  </Button>
                )}
                <Button variant="ghost" size="icon" onClick={() => setShowDropdown(false)}>
                  <X size={16} />
                </Button>
              </div>
            </div>

            {/* Alerts List */}
            <div className="max-h-96 overflow-y-auto">
              {alerts.length === 0 ? (
                <div className="p-4 text-center">
                  <CheckCircle className={`mx-auto mb-2 ${isDark ? 'text-green-400' : 'text-green-500'}`} size={32} />
                  <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>لا توجد تنبيهات</p>
                </div>
              ) : (
                alerts.map((alert) => (
                  <div
                    key={alert.id}
                    className={`p-3 border-b ${isDark ? 'border-slate-700 hover:bg-slate-700' : 'border-gray-100 hover:bg-gray-50'} cursor-pointer ${!alert.is_read ? (isDark ? 'bg-slate-700/50' : 'bg-blue-50') : ''}`}
                    onClick={() => {
                      if (!alert.is_read) markAsRead(alert.id);
                    }}
                  >
                    <div className="flex items-start gap-3">
                      {getAlertIcon(alert.type)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-slate-800'}`}>
                            {alert.title}
                          </p>
                          {!alert.is_read && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                          )}
                        </div>
                        <p className={`text-xs truncate ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          {alert.message}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={`text-xs ${getSeverityBadge(alert.severity)}`}>
                            {alert.severity === 'high' ? 'حرج' : alert.severity === 'medium' ? 'متوسط' : 'منخفض'}
                          </Badge>
                          <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                            {new Date(alert.created_at).toLocaleDateString('ar-IQ')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            <div className={`p-2 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
              <Link to="/smart-alerts" onClick={() => setShowDropdown(false)}>
                <Button variant="ghost" className="w-full text-sm">
                  عرض جميع التنبيهات
                </Button>
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default NotificationBell;
